import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

type UseHistoryStateState<T> = {
  past: T[];
  present: T;
  future: T[];
};

function useHistoryState<T extends object>({
  initialValue,
}: {
  initialValue: T;
}) {
  const initialValueRef = useRef(initialValue);

  const [state, setState] = useState<UseHistoryStateState<T>>({
    past: [],
    future: [],
    present: initialValueRef.current,
  });
  const stateRef = useRef<UseHistoryStateState<T>>(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const canUndo = state.past.length !== 0;
  const canRedo = state.future.length !== 0;

  const undo = useCallback(() => {
    if (canUndo) {
      setState(({ past, present, future }) => {
        return {
          past: past.slice(0, past.length - 1),
          present: past[past.length - 1],
          future: present ? [present, ...future] : future,
        };
      });
    }
  }, [canUndo]);

  const redo = useCallback(() => {
    if (canRedo) {
      setState(({ past, present, future }) => {
        return {
          past: present ? [...past, present] : past,
          present: future[0],
          future: future.slice(1),
        };
      });
    }
  }, [canRedo]);

  const set = useCallback(
    (newPresent: SetStateAction<T>, replace?: boolean) => {
      setState(({ past, present, future }) => {
        if (newPresent === present) {
          return { past, present, future };
        }

        if (replace) {
          return {
            past,
            present: structuredClone(
              typeof newPresent === "function"
                ? newPresent(present)
                : newPresent,
            ),
            future: [],
          };
        }

        return {
          past: present ? [...past, present] : past,
          present: structuredClone(
            typeof newPresent === "function" ? newPresent(present) : newPresent,
          ),
          future: [],
        };
      });
    },
    [],
  );

  const clear = useCallback((newPresent?: T) => {
    setState({
      past: [],
      future: [],
      present: newPresent ?? initialValueRef.current,
    });
  }, []);

  return { state: state.present, set, undo, redo, clear, canUndo, canRedo };
}

export default useHistoryState;
